import React, { useCallback, useEffect, useState } from 'react';
import { Construction, Download, Upload, NotebookPen, CheckCircle2, X, MapPinHouse, Clock, Trash2 } from 'lucide-react';
import '../styles/components/_docsincorporadoras.scss';
import '../styles/components/_docsenterprise.scss';
import { ToastContainer, toast } from "react-toastify";
import Unit from "../domain/entities/unit";
import Tooltip from './tootip/Tootip';
import ConstructionCompanyProvider from '../infra/provider/constructionCompanyProvider';
import { Address } from '../domain/entities/address';
import UploadImageRepository from '../infra/repository/uploadImageRepository';
import ObservationsPanel from './observations/ObservationsPanel';
import { Observation } from './observations/ObservationsPanel';

// Funções utilitárias para conversão de datas
const dateToUnixTimestamp = (dateString: string): number => {
    const date = new Date(dateString);
    // Definir para o final do dia (23:59:59)
    date.setHours(23, 59, 59, 999);
    return Math.floor(date.getTime() / 1000);
};

const formatUnixTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('pt-BR');
};

interface Enterprise {
    id: number;
    name: string;
}
interface Document {
    id: number;
    code: string;
    name: string;
    status: 'Não enviado' | 'Em análise' | 'Aprovado' | 'Reprovado' | 'Não aplicável';
    observation?: string;
    uploaded: boolean;
    fileType?: string;
    path?: string;
    dueDate?: string;
    expiresAt?: number;
    expiresAtFormatted?: string;
}
interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (observation: string) => void;
    currentObservation: string;
}
interface AddDocumentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (name: string) => void;
    selectedEnterpriseId: string;
}

interface Incorporadora {
    id: string;
    createdAt: string;
    cnpj: string;
    razaoSocial: string;
    responsibleName: string;
    email: string;
    phone: string;
    cpf: string;
    address: Address;
    status: "active" | "inactive";
}

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({ isOpen, onClose, onAdd, selectedEnterpriseId }) => {
    const [documentName, setDocumentName] = useState('');
    const [currentDoc, setCurrentDoc] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [fileType, setFileType] = useState('');
    const constructionCompanyProvider = ConstructionCompanyProvider.getInstance();

    if (!isOpen) return null;

    const handleCleanDocuments = () => {
        setDocumentName('');
        setCurrentDoc(null);
        setFileType('');
    }

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            if (!currentDoc) {
                toast.error("Por favor, faça o upload do documento antes de adicionar.");
                setIsLoading(false);
                return;
            }

            // Primeiro, fazer o upload do arquivo
            const uploadImageRepository = UploadImageRepository.getInstance();
            const documentUrl = await uploadImageRepository.upload(currentDoc);

            // Depois, criar o documento com a URL retornada
            // Definir data de expiração para 1 ano a partir de agora
            const oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
            const expiresAtTimestamp = dateToUnixTimestamp(oneYearFromNow.toISOString().split('T')[0]);

            await constructionCompanyProvider.postConstructionCompanyDocumentById(
                selectedEnterpriseId,
                'EX',
                documentName,
                documentUrl,
                expiresAtTimestamp
            );

            // Atualizar o status para 'under_review' (Em análise) imediatamente após criar o documento
            await constructionCompanyProvider.putConstructionCompanyDocumentById(
                selectedEnterpriseId,
                'EX',
                'under_review'
            );

            onAdd(documentName);
            toast.success("Documento adicionado com sucesso!");
            handleCleanDocuments();
            onClose();
        } catch (error) {
            console.error('Erro ao adicionar documento:', error);
            toast.error("Erro ao adicionar documento.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpload = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf,.xlsx,.xlsm,.png,.jpg,.jpeg';

        input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
                const fileExtension = file.name.toLowerCase().split('.').pop();
                const allowedExtensions = ['pdf', 'xlsx', 'xlsm', 'png', 'jpg', 'jpeg'];

                if (!allowedExtensions.includes(fileExtension || '')) {
                    toast.error('Formato de arquivo não suportado. Use XLSX, XLSM, PDF, PNG ou JPG/JPEG');
                    return;
                }

                setCurrentDoc(file);
                setFileType(fileExtension || '');
                toast.success("Arquivo selecionado com sucesso!");
            }
        };
        input.click();
    };

    return (
        <div className="modal-overlay-unidade">
            <div className="modal-unidade">
                <div className="modal-header-unidade">
                    <h2>Adicionar novo documento</h2>
                    <button className="close-button-unidade" onClick={onClose}>✕</button>
                </div>
                <p>Digite o nome do novo documento.</p>
                <div className="divisor-enterprise"></div>
                <div className='form-group'>
                    <input
                        value={documentName}
                        onChange={(e) => setDocumentName(e.target.value)}
                        placeholder="Nome do documento"
                    />
                    <button
                        onClick={() => handleUpload()}
                        className="upload-button-incorp"
                        title="Upload Documento"
                    >
                        <Upload size={10} />
                        <span className="upload-button-text"> Upload do documento</span>
                    </button>
                </div>

                <div className="form-buttons-unidade">
                    <button
                        type="button"
                        className="cancel-button-unidade"
                        onClick={onClose}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className={`create-button-unidade ${isLoading ? "loading" : ""}`}
                        disabled={isLoading || !documentName.trim()}
                        onClick={handleSubmit}
                    >
                        {isLoading ? "Salvando..." : "Adicionar"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ObservationModal: React.FC<ModalProps> = ({ isOpen, onClose, onSave, currentObservation }) => {
    const [observation, setObservation] = useState(currentObservation);
    const [isLoading, setIsLoading] = useState(false);

    if (!isOpen) return null;

    const handleSave = () => {
        setIsLoading(true);
        onSave(observation);
        setIsLoading(false);
        onClose();
    };

    return (
        <div className="modal-overlay-unidade">
            <div className="modal-unidade">
                <div className="modal-header-unidade">
                    <h2>Adicionar observação:</h2>
                    <button className="close-button-unidade" onClick={onClose}>✕</button>
                </div>
                <p>Adicione uma observação para justificar a invalidação do documento.</p>
                <div className="divisor-enterprise"></div>
                <div className='form-group'>
                    <input
                        value={observation}
                        onChange={(e) => setObservation(e.target.value)}
                        placeholder="Digite o motivo da invalidação do documento."
                    />
                </div>

                <div className="form-buttons-unidade">
                    <button
                        type="button"
                        className="cancel-button-unidade"
                        onClick={onClose}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className={`create-button-unidade ${isLoading ? "loading" : ""}`}
                        disabled={isLoading}
                        onClick={handleSave}
                    >
                        {isLoading ? "Salvando..." : "Salvar"}
                    </button>
                </div>
            </div>
        </div>
    );
};

function DocsIncorporadores() {
    const [selectedEnterprise, setSelectedEnterprise] = useState<string>('');
    const [activeTab, setActiveTab] = useState<string>('tab1');
    const [modalOpen, setModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [documents, setDocuments] = useState<{
        docsExistentes: Document[];
        outros: Document[];
    }>({
        docsExistentes: [],
        outros: []
    });
    const [observations, setObservations] = useState<Observation[]>([]);

    const constructionCompanyProvider = ConstructionCompanyProvider.getInstance();
    const [incorporadoras, setIncorporadoras] = useState<Incorporadora[]>([]);

    const getConstructionCompanies = useCallback(async () => {
        setIsLoading(true);

        const storedData = await constructionCompanyProvider.getConstructionCompaniesAsync();
        if (storedData && storedData.length > 0) {
            const updatedData = storedData.map((company) => ({
                id: company.id,
                createdAt: company.createdAt,
                cnpj: company.document,
                razaoSocial: company.name,
                responsibleName: company.responsibleName,
                email: company.email,
                phone: company.phone,
                cpf: company.responsibleDocument,
                address: company.address,
                status: company.status as "active" | "inactive"
            }));
            setIncorporadoras(updatedData);
        }
        setIsLoading(false);
    }, [constructionCompanyProvider]);

    useEffect(() => {
        getConstructionCompanies();
    }, [getConstructionCompanies]);

    const formatCNPJ = (cnpj: string) => {
        if (cnpj === "") return "";
        return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    };

    const enterprises: Enterprise[] = incorporadoras.map((incorporadora) => ({
        id: Number(incorporadora.id),
        name: `${incorporadora.razaoSocial} | ${formatCNPJ(incorporadora.cnpj)}`
    }));

    const translateStatus = (status: Document['status']) => {
        switch (status) {
            case 'Não enviado':
                return 'not_sent';
            case 'Em análise':
                return 'under_review';
            case 'Não aplicável':
                return 'not_elegible';
            case 'Aprovado':
                return 'approved';
            case 'Reprovado':
                return 'reproved';
            default:
                return 'not_sent';
        }
    };

    const translateAPIStatus = (status: string): Document['status'] => {
        switch (status) {
            case 'not_sent':
                return 'Não enviado';
            case 'under_review':
                return 'Em análise';
            case 'not_elegible':
                return 'Não aplicável';
            case 'approved':
                return 'Aprovado';
            case 'rejected':
                return 'Reprovado';
            default:
                return 'Não enviado';
        }
    };

    const handleStatusChange = async (documentId: number, newStatus: Document['status']) => {
        const document = activeTab === 'tab1'
            ? documents.docsExistentes.find(doc => doc.id === documentId)
            : documents.outros.find(doc => doc.id === documentId);

        if (!document) return;

        // Guardar o status anterior para caso de erro
        const previousStatus = document.status;

        try {
            const document = activeTab === 'tab1'
                ? documents.docsExistentes.find(doc => doc.id === documentId)
                : documents.outros.find(doc => doc.id === documentId);

            if (!document) return;

            // Verifica se o documento foi enviado antes de permitir aprovação
            if (newStatus === 'Aprovado' && !document.uploaded) {
                toast.error("O documento precisa ser enviado antes de ser aprovado");
                return;
            }

            // Verifica se o documento está em análise antes de permitir aprovação ou reprovação
            if ((newStatus === 'Aprovado' || newStatus === 'Reprovado') && document.status !== 'Em análise') {
                toast.error("O documento precisa estar em análise antes de ser aprovado ou reprovado");
                return;
            }

            // Verifica se o documento pode ser marcado como "Em análise" apenas se foi enviado
            if (newStatus === 'Em análise' && !document.uploaded) {
                toast.error("O documento precisa ser enviado antes de ser marcado como 'Em análise'");
                return;
            }

            // Se o status for Reprovado, abre o modal de observação primeiro
            if (newStatus === 'Reprovado' || newStatus === 'Não aplicável') {
                setSelectedDoc(document);
                setModalOpen(true);
                return;
            }

            // Atualizar o estado local primeiro
            setDocuments(prev => {
                const newState = { ...prev };
                if (activeTab === 'tab1') {
                    newState.docsExistentes = prev.docsExistentes.map(doc =>
                        doc.id === documentId ? { ...doc, status: newStatus } : doc
                    );
                } else {
                    newState.outros = prev.outros.map(doc =>
                        doc.id === documentId ? { ...doc, status: newStatus } : doc
                    );
                }
                return newState;
            });

            // Fazer a chamada à API
            await constructionCompanyProvider.putConstructionCompanyDocumentById(
                selectedEnterprise,
                document.code,
                translateStatus(newStatus)
            );

            toast.success("Status do documento atualizado com sucesso!");
        } catch (error: any) {
            console.error('Erro ao atualizar status:', error);

            // Reverter o estado local em caso de erro
            setDocuments(prev => {
                const newState = { ...prev };
                if (activeTab === 'tab1') {
                    newState.docsExistentes = prev.docsExistentes.map(doc =>
                        doc.id === documentId ? { ...doc, status: previousStatus } : doc
                    );
                } else {
                    newState.outros = prev.outros.map(doc =>
                        doc.id === documentId ? { ...doc, status: previousStatus } : doc
                    );
                }
                return newState;
            });

            // Mensagem de erro mais específica
            if (error.message === "Status is not allowed") {
                toast.error("Este status não é permitido para este documento.");
            } else {
                toast.error("Este status não é permitido para este documento. Por favor, verifique as regras de aprovação.");
            }
        }
    };

    const handleDownload = async (documentId: number) => {
        try {
            const document = activeTab === 'tab1'
                ? documents.docsExistentes.find(doc => doc.id === documentId)
                : documents.outros.find(doc => doc.id === documentId);

            if (!document) {
                toast.error("Documento não encontrado");
                return;
            }

            // Se tiver base64 local, usa ele
            if (document.path) {
                try {
                    const byteCharacters = atob(document.path);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);

                    let mimeType = 'application/pdf';
                    if (document.fileType === 'png') mimeType = 'image/png';
                    if (document.fileType === 'jpg' || document.fileType === 'jpeg') mimeType = 'image/jpeg';

                    const blob = new Blob([byteArray], { type: mimeType });
                    const url = window.URL.createObjectURL(blob);
                    const link = window.document.createElement('a');
                    link.href = url;
                    link.download = `${document.name || 'documento'}.${document.fileType || 'pdf'}`;
                    window.document.body.appendChild(link);
                    link.click();
                    window.document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast.success("Download iniciado com sucesso!");
                    return;
                } catch (error) {
                    console.error('Erro ao processar base64:', error);
                }
            }

            // Se não tiver base64 local ou falhar, tenta buscar do backend
            const response = await constructionCompanyProvider.getConstructionCompanyDocumentById(selectedEnterprise);
            const doc = response.find(doc => doc.code === document.code);

            if (doc && doc.path) {
                const link = window.document.createElement('a');
                link.href = doc.path;
                link.download = `${document.name || 'documento'}.${document.fileType || 'pdf'}`;
                window.document.body.appendChild(link);
                link.click();
                window.document.body.removeChild(link);
                toast.success("Download iniciado com sucesso!");
            } else {
                toast.error("Documento não encontrado no servidor");
            }
        } catch (error) {
            console.error('Erro ao baixar documento:', error);
            toast.error("Erro ao baixar documento");
        }
    };

    const handleUpload = (documentId: number) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf,.xlsx,.xlsm,.png,.jpg,.jpeg';

        input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
                const fileExtension = file.name.toLowerCase().split('.').pop();
                const allowedExtensions = ['pdf', 'xlsx', 'xlsm', 'png', 'jpg', 'jpeg'];

                if (!allowedExtensions.includes(fileExtension || '')) {
                    toast.error('Formato de arquivo não suportado. Use XLSX, XLSM, PDF, PNG ou JPG/JPEG');
                    return;
                }

                try {
                    const uploadImageRepository = UploadImageRepository.getInstance();
                    const documentUrl = await uploadImageRepository.upload(file);
                    if (!documentUrl) {
                        throw new Error('Falha ao fazer upload do arquivo');
                    }
                    await handleSubmit(documentId, documentUrl, fileExtension || '');
                } catch (error) {
                    console.error('Erro ao fazer upload do arquivo:', error);
                    toast.error('Erro ao fazer upload do arquivo');
                }
            }
        };
        input.click();
    };

    const handleSubmit = async (documentId: number, base64String: string, fileType: string) => {
        setIsLoading(true);
        try {
            const document = activeTab === 'tab1'
                ? documents.docsExistentes.find(doc => doc.id === documentId)
                : documents.outros.find(doc => doc.id === documentId);

            if (!document) {
                toast.error("Documento não encontrado");
                return;
            }

            // Primeiro, enviar o documento
            // Definir data de expiração para 1 ano a partir de agora
            const oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
            const expiresAtTimestamp = dateToUnixTimestamp(oneYearFromNow.toISOString().split('T')[0]);

            await constructionCompanyProvider.postConstructionCompanyDocumentById(
                selectedEnterprise,
                document.code,
                document.name,
                base64String,
                expiresAtTimestamp
            );

            // Depois, atualizar o status para 'under_review' (Em análise)
            await constructionCompanyProvider.putConstructionCompanyDocumentById(
                selectedEnterprise,
                document.code,
                'under_review'
            );

            // Atualizar o documento localmente
            setDocuments(prev => {
                const newState = { ...prev };
                const updatedDoc: Document = {
                    ...document,
                    uploaded: true,
                    fileType,
                    status: 'Em análise',
                    path: base64String
                };

                if (activeTab === 'tab1') {
                    newState.docsExistentes = prev.docsExistentes.map(doc =>
                        doc.id === documentId ? updatedDoc : doc
                    );
                } else {
                    newState.outros = prev.outros.map(doc =>
                        doc.id === documentId ? updatedDoc : doc
                    );
                }
                return newState;
            });

            toast.success("Documento enviado e enviado para análise!");
        } catch (error) {
            console.error('Erro ao enviar documento:', error);
            toast.error("Erro ao enviar documento");
        } finally {
            setIsLoading(false);
        }
    };

    const handleObservationClick = (doc: Document) => {
        if (doc.status === 'Reprovado' || doc.status === 'Não aplicável') {
            setSelectedDoc(doc);
            setModalOpen(true);
        }
    };

    const handleAddDocument = async (name: string) => {
        try {
            if (!selectedEnterprise) {
                toast.error("Selecione uma incorporadora primeiro");
                return;
            }

            if (!name.trim()) {
                toast.error("Digite um nome para o documento");
                return;
            }

            // Primeiro, criar o documento com status 'not_sent'
            // await constructionCompanyProvider.postConstructionCompanyDocumentById(
            //     selectedEnterprise,
            //     'new',
            //     name.trim(),
            //     null
            // );

            // Atualizar o status para 'sent'
            // await constructionCompanyProvider.putConstructionCompanyDocumentById(
            //     selectedEnterprise,
            //     'new',
            //     'sent'
            // );

            // Recarregar os documentos
            await reloadDocuments();

            // Mudar para a aba Outros
            setActiveTab('tab2');

            toast.success("Documento adicionado com sucesso!");
        } catch (error) {
            console.error('Erro ao adicionar documento:', error);
            toast.error("Erro ao adicionar documento");
        }
    };

    const reloadDocuments = async () => {
        try {
            if (selectedEnterprise) {
                console.log('=== DEBUG reloadDocuments ===');
                const response = await constructionCompanyProvider.getConstructionCompanyDocumentById(selectedEnterprise);
                console.log('Resposta completa da API:', response);

                if (response) {
                    const existingDocs: Document[] = [];
                    const otherDocs: Document[] = [];

                    response.forEach((doc) => {
                        console.log('Documento recebido da API:', doc);
                        const status = translateAPIStatus(doc.status);
                        const newDoc: Document = {
                            id: Number(doc.id),
                            code: doc.code,
                            name: doc.description,
                            status: status,
                            observation: doc.obs,
                            uploaded: status !== 'Não enviado' && status !== 'Não aplicável',
                            dueDate: doc.dueDate,
                            expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                            expiresAtFormatted: doc.expiresAtFormatted
                        };
                        console.log('Documento processado:', newDoc);

                        if (doc.code.startsWith('EX')) {
                            otherDocs.push(newDoc);
                        } else {
                            existingDocs.push(newDoc);
                        }
                    });

                    console.log('Documentos finais:', { existingDocs, otherDocs });
                    setDocuments({
                        docsExistentes: existingDocs,
                        outros: otherDocs
                    });
                }
            }
        } catch (error) {
            console.error('Erro ao recarregar documentos:', error);
            toast.error("Erro ao atualizar lista de documentos");
        }
    };

    const saveObservation = async (observation: string) => {
        if (selectedDoc) {
            try {
                // Determinar o status correto baseado no status atual do documento
                const targetStatus = selectedDoc.status === 'Reprovado' ? 'reproved' : 'not_elegible';

                // Atualizar o estado local
                const activeDocuments = getActiveDocuments();
                const newDocs = activeDocuments.map(doc =>
                    doc.id === selectedDoc.id ? { ...doc, observation } : doc
                );
                updateDocuments(newDocs);

                // Fazer a chamada à API com a observação
                await constructionCompanyProvider.putConstructionCompanyDocumentById(
                    selectedEnterprise,
                    selectedDoc.code,
                    targetStatus,
                    observation
                );

                await reloadDocuments();
                setModalOpen(false);
                toast.success("Status e observação atualizados com sucesso!");
            } catch (error) {
                console.error('Erro ao salvar observação:', error);
                toast.error("Erro ao salvar observação");
                await reloadDocuments();
            }
        }
    };



    const handleExpiresAtChange = async (documentId: number, expiresAt: string) => {
        console.log('=== DEBUG handleExpiresAtChange ===');
        console.log('Parâmetros:', { documentId, expiresAt });

        const document = activeTab === 'tab1'
            ? documents.docsExistentes.find(doc => doc.id === documentId)
            : documents.outros.find(doc => doc.id === documentId);

        console.log('Documento encontrado:', document);

        if (!document) {
            return;
        }

        if (!expiresAt) {
            return;
        }

        // Guardar o valor original para caso de erro
        const originalExpiresAt = document.expiresAt;

        try {

            // Converter a data para timestamp Unix usando a função utilitária
            const expiresAtTimestamp = dateToUnixTimestamp(expiresAt);
            console.log('Timestamp gerado:', {
                originalDate: expiresAt,
                expiresAtTimestamp: expiresAtTimestamp,
                type: typeof expiresAtTimestamp,
                asString: expiresAtTimestamp.toString()
            });

            // Validar se a data não é anterior à data atual
            const selectedDate = new Date(expiresAt);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                toast.error("A data de vencimento não pode ser anterior à data atual");
                return;
            }

            // Atualizar o estado local primeiro
            setDocuments(prev => {
                const newState = { ...prev };
                if (activeTab === 'tab1') {
                    newState.docsExistentes = prev.docsExistentes.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: expiresAtTimestamp } : doc
                    );
                } else {
                    newState.outros = prev.outros.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: expiresAtTimestamp } : doc
                    );
                }
                console.log('Estado atualizado localmente:', newState);
                return newState;
            });

            // Fazer a chamada à API
            try {
                console.log('Chamando API com:', {
                    selectedEnterprise,
                    code: document.code,
                    status: translateStatus(document.status),
                    observation: document.observation,
                    expiresAt: expiresAtTimestamp
                });

                const response = await constructionCompanyProvider.putConstructionCompanyDocumentById(
                    selectedEnterprise,
                    document.code,
                    translateStatus(document.status),
                    document.observation,
                    expiresAtTimestamp
                );

                console.log('Resposta da API:', response);
                toast.success("Data de vencimento atualizada com sucesso!");

                // Recarregar documentos para confirmar que foi salvo
                console.log('Recarregando documentos para confirmar...');
                await reloadDocuments();

            } catch (apiError) {
                console.error('Erro na chamada da API:', apiError);
                throw apiError; // Re-throw para ser capturado pelo catch externo
            }
        } catch (error) {
            console.error('Erro ao atualizar data de vencimento:', error);
            toast.error("Erro ao atualizar data de vencimento");
            // Reverter as alterações locais em caso de erro
            setDocuments(prev => {
                const newState = { ...prev };
                if (activeTab === 'tab1') {
                    newState.docsExistentes = prev.docsExistentes.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: originalExpiresAt } : doc
                    );
                } else {
                    newState.outros = prev.outros.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: originalExpiresAt } : doc
                    );
                }
                return newState;
            });
        }
    };

    const handleClearDate = async (documentId: number) => {
        const docItem = getActiveDocuments().find(doc => doc.id === documentId);
        if (!docItem) return;

        try {
            // Atualizar o estado local primeiro
            const updatedDocuments = getActiveDocuments().map(doc =>
                doc.id === documentId
                    ? { ...doc, expiresAt: undefined, expiresAtFormatted: undefined }
                    : doc
            );
            updateDocuments(updatedDocuments);

            // Enviar para o backend usando a nova rota DELETE
            await constructionCompanyProvider.deleteConstructionCompanyDocumentExpiresAtById(
                documentId.toString()
            );

            toast.success("Data de vencimento removida com sucesso!");

            // Não recarregar documentos para manter as alterações locais
            // await reloadDocuments();
        } catch (error) {
            console.error('Erro ao remover data de vencimento:', error);
            toast.error("Erro ao remover data de vencimento");
            // Reverter as alterações locais em caso de erro
            setDocuments(prev => {
                const newState = { ...prev };
                if (activeTab === 'tab1') {
                    newState.docsExistentes = prev.docsExistentes.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: docItem.expiresAt, expiresAtFormatted: docItem.expiresAtFormatted } : doc
                    );
                } else {
                    newState.outros = prev.outros.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: docItem.expiresAt, expiresAtFormatted: docItem.expiresAtFormatted } : doc
                    );
                }
                return newState;
            });
        }
    };

    const getActiveDocuments = () => {
        switch (activeTab) {
            case 'tab1': return documents.docsExistentes;
            case 'tab2': return documents.outros;
            default: return [];
        }
    };

    const updateDocuments = (newDocs: Document[]) => {
        setDocuments(prev => {
            const newState = { ...prev };
            switch (activeTab) {
                case 'tab1': newState.docsExistentes = newDocs; break;
                case 'tab2': newState.outros = newDocs; break;
            }
            return newState;
        });
    };

    const handleCleanDocuments = () => {
        setDocuments({
            docsExistentes: [],
            outros: []
        });
    }

    const handleEnterpriseChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        handleCleanDocuments();

        try {
            const selectedValue = e.target.value;
            setSelectedEnterprise(selectedValue);
            if (selectedValue) {
                const response = await constructionCompanyProvider.getConstructionCompanyDocumentById(selectedValue);
                if (response) {
                    const existingDocs: Document[] = [];
                    const otherDocs: Document[] = [];

                    response.forEach((doc) => {
                        const status = translateAPIStatus(doc.status);
                        const newDoc: Document = {
                            id: Number(doc.id),
                            code: doc.code,
                            name: doc.description,
                            status: status,
                            observation: doc.obs,
                            uploaded: status !== 'Não enviado' && status !== 'Não aplicável',
                            dueDate: doc.dueDate,
                            expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                            expiresAtFormatted: doc.expiresAtFormatted
                        };

                        // Separar os documentos baseado no código
                        if (doc.code.startsWith('EX')) {
                            otherDocs.push(newDoc);
                        } else {
                            existingDocs.push(newDoc);
                        }
                    });

                    // Atualizar os documentos mantendo a separação
                    setDocuments({
                        docsExistentes: existingDocs,
                        outros: otherDocs
                    });

                    toast.success(`Documentos carregados com sucesso!`);
                }
            }
        } catch (error) {
            toast.error("Erro ao carregar documentos.");
        }
    };

    const renderTable = (documents: Document[]) => (
        <>
            {activeTab === 'tab2' && (
                <div className="add-document-button-container" style={{ marginBottom: '1rem', textAlign: 'right' }}>
                    <button
                        onClick={() => setAddModalOpen(true)}
                        className="add-button-plan-viab">
                        + Adicionar documento
                    </button>
                </div>
            )}
            <table className="docs-table-incorp">
                <thead>
                    <tr>
                        <th>Situação</th>
                        <th>Nome do Documento</th>
                        <th>Status</th>
                        <th>Vencimento</th>
                        <th>Observação</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {documents.map((doc) => (
                        <tr key={doc.id}>
                            <td>
                                <div className="icon-name-incorp">
                                    {doc.status === 'Reprovado' || doc.status === 'Não aplicável' ? (
                                        <X size={20} className="x-icon-incorp" />
                                    ) : doc.status === 'Aprovado' ? (
                                        <CheckCircle2 size={20} className="check-icon-incorp" />
                                    ) : doc.status === 'Em análise' ? (
                                        <Clock size={20} className="clock-icon-incorp" />
                                    ) : (
                                        <Clock size={20} className="clock-icon-incorp" />
                                    )}
                                </div>

                            </td>
                            <td className="document-name-incorp">
                                {doc.name}
                            </td>
                            <td>
                                <select
                                    value={doc.status}
                                    onChange={(e) => handleStatusChange(doc.id, e.target.value as Document['status'])}
                                    className="status-select-incorp"
                                >
                                    <option value="Não enviado">Não enviado</option>
                                    <option value="Em análise">Em análise</option>
                                    <option value="Não aplicável">Não aplicável</option>
                                    <option value="Aprovado">Aprovado</option>
                                    <option value="Reprovado">Reprovado</option>
                                </select>
                            </td>
                            <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="date"
                                        value={doc.expiresAt ? new Date(doc.expiresAt * 1000).toISOString().split('T')[0] : ''}
                                        onChange={(e) => handleExpiresAtChange(doc.id, e.target.value)}
                                        className="due-date-input-incorp"
                                        min={new Date().toISOString().split('T')[0]}
                                        title={doc.expiresAtFormatted || (doc.expiresAt ? formatUnixTimestamp(doc.expiresAt) : 'Data não definida')}
                                    />
                                    {(doc.expiresAt) && (
                                        <Tooltip text="Limpar">
                                            <button
                                                onClick={() => handleClearDate(doc.id)}
                                                className="clear-date-button"
                                                title="Limpar"
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '4px',
                                                    borderRadius: '4px',
                                                    color: '#666',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </Tooltip>
                                    )}
                                </div>
                            </td>
                            <td className="observation-cell-incorp">
                                {doc.observation || '-'}
                            </td>
                            <td className="actions-cell-incorp">
                                <Tooltip text="Upload">
                                    <button
                                        onClick={() => handleUpload(doc.id)}
                                        className="action-button"
                                        title="Upload"
                                    >
                                        <Upload size={20} />
                                    </button>
                                </Tooltip>
                                <Tooltip text="Download">
                                    <button
                                        onClick={() => handleDownload(doc.id)}
                                        className="action-button"
                                        title="Download"
                                    >
                                        <Download size={20} />
                                    </button>
                                </Tooltip>
                                {/* <Tooltip text="Observação">
                                    <button
                                        onClick={() => handleObservationClick(doc)}
                                        className={`action-button ${(doc.status !== 'Reprovado' && doc.status !== 'Não aplicável') ? 'disabled' : ''}`}
                                        disabled={doc.status !== 'Reprovado' && doc.status !== 'Não aplicável'}
                                        title="Observação"
                                    >
                                        <NotebookPen size={20} />
                                    </button>
                                </Tooltip> */}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );

    return (
        <div className="container-docsenterprises-incorp">
            <div className="select-container-incorp">
                <label htmlFor="enterprise-select" className="select-label-incorp">
                    Selecione uma incorporadora para visualizar os documentos:
                </label>
                <select
                    id="enterprise-select-incorp"
                    className="enterprise-select-incorp"
                    value={selectedEnterprise}
                    onChange={(e) => handleEnterpriseChange(e)}
                >
                    <option value="" disabled>Selecione uma incorporadora</option>
                    {enterprises.map((enterprise) => (
                        <option key={enterprise.id} value={enterprise.id}>
                            {enterprise.name}
                        </option>
                    ))}
                </select>
            </div>

            {!selectedEnterprise ? (
                <div className="empty-state-incorp">
                    <MapPinHouse size={150} className="empty-icon-incorp" />
                    <p className="empty-text-incorp">
                        Selecione uma incorporadora para visualizar os resultados.
                    </p>
                </div>
            ) : (
                <>
                    <div className="tabs-navigation">
                        <button
                            className={`tab-button ${activeTab === 'tab1' ? 'active' : ''}`}
                            onClick={() => setActiveTab('tab1')}
                        >
                            Documentos existentes
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'tab2' ? 'active' : ''}`}
                            onClick={() => setActiveTab('tab2')}
                        >
                            Outros
                        </button>
                    </div>
                    {renderTable(getActiveDocuments())}
                </>
            )}

            <ObservationModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={saveObservation}
                currentObservation={selectedDoc?.observation || ''}
            />

            <AddDocumentModal
                isOpen={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onAdd={handleAddDocument}
                selectedEnterpriseId={selectedEnterprise}
            />

            <ToastContainer />

            {/* Componente de Observações */}
            {selectedEnterprise && (
                <ObservationsPanel
                    entityId={selectedEnterprise}
                    entityType="incorporadora"
                    onObservationChange={setObservations}
                />
            )}
        </div>
    );
}

export default DocsIncorporadores;